// client/baseqlClient.ts
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { distinctUntilChanged, filter, switchMap } from "rxjs/operators";
import { SubscriptionDedupe } from "./requestHandlers/subscriptionDedupe.js";
import { SynchronousDedupe } from "./requestHandlers/synchronousDedupe.js";
import { UnpackHandler } from "./requestHandlers/unpack.js";
import { UpgradeHandler } from "./requestHandlers/upgrade.js";
import { BaseQLWebsocketTransport } from "./transports/websocketTransport.js";
var BaseQLClient = class {
  static {
    /** id of the currently active session. can be used to authorize certain actions against the server */
    this.sessionID = "";
  }
  static {
    /** id of the currently active connection. used to select the correct handler stack on the server */
    this.connectionID = "";
  }
  static {
    /** if we have a transport yet */
    this.hasTransport = false;
  }
  /**
   * Initialize the client with no extra intermediate handlers.
   *
   * Shortcut for
   * ```ts
   * const transportHandler = BaseQLClient.createTransportHandler();
   * BaseQLClient.initHandler(transportHandler);
   * ```
   */
  static initDefault(transportOptions) {
    const transportHandler = this.createTransportHandler(transportOptions);
    this.initHandler(transportHandler);
  }
  static isConnected() {
    return this.transport.pipe(
      filter(Boolean),
      switchMap((transport) => transport.available),
      distinctUntilChanged()
    );
  }
  static isConnectedSync() {
    return !!this.transport.getValue()?.available.getValue();
  }
  /**
   * create a `BaseQLWebsocketTransport` with the given options.
   *
   * Use this to set custom `TransportOptions` for the transport instance.
   * See the `BaseQLClient` JSDoc for more info.
   */
  static createTransportHandler(options) {
    const transport = BaseQLWebsocketTransport.get({ sessionID: localStorage.getItem("sessionID") ?? "" }, options);
    const transportHandler = new UpgradeHandler(new UnpackHandler(transport));
    this.setTransport(transport);
    return transportHandler;
  }
  static {
    this.transport = new BehaviorSubject(null);
  }
  /**
   * Get the currently used transport of the client.
   * This can be used to (re-)start or close sessions.
   */
  static getTransport() {
    return firstValueFrom(this.transport.pipe(filter(Boolean)));
  }
  static setTransport(handler) {
    this.transport.next(handler);
    this.hasTransport = true;
  }
  static {
    this.requestHandler = new BehaviorSubject(null);
  }
  /**
   * Get the currently used requestHandler of the client.
   * This can be used for direct communication with the server, but should only be used by internal functions.
   */
  static getRequestHandler() {
    return firstValueFrom(this.requestHandler.pipe(filter(Boolean)));
  }
  /**
   * Initialize the connection using the given request handler.
   * To use this you should have called `createTransportHandler` before and used its return value to create a requestHandler.
   */
  static initHandler(handler) {
    if (this.requestHandler.getValue())
      throw new Error("You cannot initialize BaseQLClient twice, make sure you only call BaseQL.initHandler once");
    if (!this.hasTransport)
      throw new Error(
        "No transport defined yet. Please construct a transportHandler with createTransportHandler and incorporate it into your requestHandler before calling this."
      );
    this.requestHandler.next(new SynchronousDedupe(new SubscriptionDedupe(handler)));
  }
};
export {
  BaseQLClient
};
